import {
  createContext,
  useState,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  useMemo,
  useCallback,
} from 'react';
import { RadioOption, SelectOption } from '@taleemabad/dsm/components';
import { IBookChapter, IExam, IQuestion } from '@taleemabad/db';

export interface SelectedOptions {
  grade: SelectOption | null;
  subject: SelectOption | null;
  selectedChapters: IBookChapter[] | null;
  examType: RadioOption | null;
  examTime: string | null;
}

interface ExamContextState {
  selectedOptions: SelectedOptions;
  setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>;
  selectedQuestions: IQuestion[];
  setSelectedQuestions: Dispatch<SetStateAction<IQuestion[]>>;
  resetSelectedOptionsAndQuestions: () => void;
  selectedExam: IExam | null;
  setSelectedExam: Dispatch<SetStateAction<IExam | null>>;
}

const defaultContextValue: ExamContextState = {
  selectedOptions: {
    grade: null,
    subject: null,
    selectedChapters: null,
    examType: null,
    examTime: null,
  },
  setSelectedOptions: () => {},
  selectedQuestions: [],
  setSelectedQuestions: () => {},
  resetSelectedOptionsAndQuestions: () => {},
  selectedExam: null,
  setSelectedExam: () => {},
};

export const ExamContext = createContext<ExamContextState>(defaultContextValue);

export const ExamProvider = ({ children }: PropsWithChildren) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    grade: null,
    subject: null,
    selectedChapters: null,
    examType: null,
    examTime: null,
  });
  const [selectedQuestions, setSelectedQuestions] = useState<IQuestion[]>([]);
  const [selectedExam, setSelectedExam] = useState<IExam | null>(null);

  const resetSelectedOptionsAndQuestions = useCallback(() => {
    setSelectedOptions({
      grade: null,
      subject: null,
      selectedChapters: [],
      examType: null,
      examTime: '',
    });
    setSelectedQuestions([]);
    setSelectedExam(null);
  }, [setSelectedOptions, setSelectedQuestions]);

  const value = useMemo(
    () => ({
      selectedOptions,
      setSelectedOptions,
      selectedQuestions,
      setSelectedQuestions,
      resetSelectedOptionsAndQuestions,
      selectedExam,
      setSelectedExam,
    }),
    [
      selectedOptions,
      selectedQuestions,
      resetSelectedOptionsAndQuestions,
      selectedExam,
    ],
  );

  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
};
